<template>
  <v-skeleton-loader
    v-if="isLoading"
    type="table"
  />

  <div v-else>
    <v-dialog
      :value="isCreating"
      width="500"
      persistent
    >
      <v-card>
        <v-card-title>
          諸費ラベル
        </v-card-title>

        <v-card-text>
          <v-radio-group
            v-model="addTarget.type"
            row
          >
            <v-radio
              v-for="(selectTag, selectTagIdx) in selectTags"
              :key="`selectTags[${selectTagIdx}]`"
              :label="selectTag.text"
              :value="selectTag.value"
            ></v-radio>
          </v-radio-group>

          <v-text-field
            v-model="addTarget.name"
            :error="!addTarget.name"
            label="名前"
            required
          />
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            :ripple="false"
            @click="create"
          >
            作成
          </v-btn>
          <v-btn
            color="blue darken-1"
            text
            :ripple="false"
            @click="$emit('close')"
          >
            キャンセル
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-data-table
      ref="dataTable"
      :headers="headers"
      :items="expenseTags"
      group-by="displayType"
      item-key="kid"
      :header-props="{ sortByText: 'ソート' }"
      hide-default-footer
      disable-pagination
      no-data-text="データがありません"
    >
      <template #[`item.attributes.name`]="{item}">
        <yona-edit-dialog
          :display-name="item.name"
          @open="edit(item)"
          @save="update"
        >
          <template #input>
            <v-text-field
              v-if="isEditing"
              v-model="editTarget.name"
              placeholder="名前"
            />
          </template>
        </yona-edit-dialog>
      </template>

      <template #[`item.visible`]="{item}">
        <v-switch
          :input-value="item.visible"
          dense
          hide-details
          @change="val => updateVisible(item, val)"
        />
      </template>

      <template #[`group.header`]="{group}">
        <td :colspan="headers.length">
          {{ group }}
        </td>
      </template>

      <template #[`item.actions`]="{item}">
        <yona-edit-dialog
          title="削除の確認"
          btn-color="error"
          save-text="同意の上で削除"
          @save="destroy(item)"
        >
          <template #append-outer-display-name>
            <v-btn
              icon
              :ripple="false"
            >
              <v-icon small>
                {{ icons.mdiTrashCanOutline }}
              </v-icon>
            </v-btn>
          </template>

          <template #input>
            <span>
              このラベルに関連しているすべての諸費データも削除します。削除を実行してもよろしいですか
            </span>
          </template>
        </yona-edit-dialog>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import {
  ref,
  reactive,
  getCurrentInstance,
} from '@vue/composition-api'
import {
  mdiTrashCanOutline,
  mdiPlus,
} from '@mdi/js'
import ExpensePrimaryLabelApi from '@/api/v2/eod-closing/ExpensePrimaryLabel'
import ExpenseSecondaryLabelApi from '@/api/v2/eod-closing/ExpenseSecondaryLabel'
import ExpenseTertiaryLabelApi from '@/api/v2/eod-closing/ExpenseTertiaryLabel'
import ExpenseQuaternaryLabelApi from '@/api/v2/eod-closing/ExpenseQuaternaryLabel'
import YonaEditDialog from '@/views/components/util/YonaEditDialog.vue'

export default {
  components: {
    YonaEditDialog,
  },
  props: {
    isCreating: {
      type: Boolean,
      default: false,
    },
  },
  setup(_, { emit }) {
    const vm = getCurrentInstance().proxy

    const selectTags = [
      { text: '主分類', value: 'expense_primary_label' },
      { text: '副分類', value: 'expense_secondary_label' },
      { text: '第三分類', value: 'expense_tertiary_label' },
      { text: '第四分類', value: 'expense_quaternary_label' },
    ]

    const isLoading = ref(false)
    const isEditing = ref(false)
    const expenseTags = ref([])

    const editTarget = reactive({ id: null, name: null, type: '' })
    const addTarget = reactive({ name: '', type: selectTags[0].value })

    const getApiByType = type => {
      switch (type) {
        case 'expense_primary_label': return ExpensePrimaryLabelApi
        case 'expense_secondary_label': return ExpenseSecondaryLabelApi
        case 'expense_tertiary_label': return ExpenseTertiaryLabelApi
        case 'expense_quaternary_label': return ExpenseQuaternaryLabelApi
        default: return null
      }
    }

    const setType = (type, displayTypeName) => label => ({
      id: label.id,
      name: label.attributes.name,
      visible: label.attributes.visible,
      displayType: displayTypeName,
      kid: `${type}_${label.id}`,
      type,
    })

    const updateRefTags = ({
      primary, secondary, tertiary, quaternary,
    }) => {
      expenseTags.value = [
        ...primary.map(setType('expense_primary_label', '主分類')),
        ...secondary.map(setType('expense_secondary_label', '副分類')),
        ...tertiary.map(setType('expense_tertiary_label', '第三分類')),
        ...quaternary.map(setType('expense_quaternary_label', '第四分類')),
      ]
    }

    const getExpenseTags = async () => {
      isLoading.value = true

      try {
        const [resPrimary, resSecondary, resTertiary, resQuaternary] = await Promise.all([
          ExpensePrimaryLabelApi.getLabels(),
          ExpenseSecondaryLabelApi.getLabels(),
          ExpenseTertiaryLabelApi.getLabels(),
          ExpenseQuaternaryLabelApi.getLabels(),
        ])

        updateRefTags({
          primary: resPrimary?.data?.status === 'success' ? resPrimary.data.data.data : [],
          secondary: resSecondary?.data?.status === 'success' ? resSecondary.data.data.data : [],
          tertiary: resTertiary?.data?.status === 'success' ? resTertiary.data.data.data : [],
          quaternary: resQuaternary?.data?.status === 'success' ? resQuaternary.data.data.data : [],
        })
      } finally {
        isLoading.value = false
      }
    }

    const edit = item => {
      isEditing.value = true

      editTarget.id = item.id
      editTarget.name = item.name
      editTarget.type = item.type
    }

    const create = async () => {
      if (!addTarget.name || !addTarget.type) return

      const api = getApiByType(addTarget.type)
      if (!api) return

      try {
        const res = await api.createLabel(addTarget.name)

        if (res?.data?.status === 'success') {
          await getExpenseTags()
          vm.$toast.success('諸費ラベルを作成しました')
          addTarget.name = ''
          addTarget.type = selectTags[0].value
          emit('close')
        }
      } catch (e) {
        vm.$toast.error('作成に失敗しました')
      }
    }

    const update = async () => {
      const { id, name, type } = editTarget
      const api = getApiByType(type)
      if (!api) return

      try {
        const res = await api.updateLabelName(id, name)

        if (res?.data?.status === 'success') {
          await getExpenseTags()
          vm.$toast.success('諸費ラベルを更新しました')
        }
      } catch (e) {
        vm.$toast.error('更新に失敗しました')
      } finally {
        isEditing.value = false
        editTarget.id = null
        editTarget.name = ''
        editTarget.type = ''
      }
    }

    const destroy = async ({ id, type }) => {
      const api = getApiByType(type)
      if (!api) return

      try {
        await api.destroyLabel(id)
        await getExpenseTags()
        vm.$toast.success('諸費ラベルを削除しました')
      } catch (e) {
        vm.$toast.error('削除に失敗しました')
      }
    }

    const updateVisible = async (item, visible) => {
      const api = getApiByType(item.type)
      if (!api) return

      expenseTags.value = expenseTags.value.map(t => (t.kid === item.kid ? { ...t, visible } : t))

      try {
        await api.updateLabel(item.id, visible)
      } catch (e) {
        expenseTags.value = expenseTags.value.map(t => (t.kid === item.kid ? { ...t, visible: !visible } : t))
        vm.$toast.error('更新に失敗しました')
      }
    }

    getExpenseTags()

    return {
      // data
      isLoading,
      isEditing,
      expenseTags,
      headers: [
        { text: '名前', value: 'attributes.name' },
        { text: '表示', value: 'visible', sortable: false },
        {
          text: '',
          value: 'actions',
          align: 'end',
          sortable: false,
        },
      ],
      selectTags,
      editTarget,
      addTarget,

      // methods
      edit,
      update,
      destroy,
      create,
      updateVisible,

      icons: {
        mdiTrashCanOutline,
        mdiPlus,
      },
    }
  },
}
</script>
